import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import './App.css';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import OnboardingWizard from './pages/OnboardingWizard';
import StudentDashboard from './pages/StudentDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminSetup from './pages/AdminSetup';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import CookiePage from './pages/CookiePage';
import TestOverlay from './components/TestOverlay';
import axios from 'axios';
import { initLocations } from './data/austrianLocations';

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Suppress ResizeObserver loop error - this is a browser warning, not a real error
const resizeObserverErr = window.console.error;
window.console.error = (...args) => {
  if (args[0]?.includes?.('ResizeObserver loop')) return;
  resizeObserverErr(...args);
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTestOverlay, setShowTestOverlay] = useState(false);

  useEffect(() => {
    Promise.all([checkAuth(), initLocations()]).finally(() => {
      // Both auth check and location pre-load done
    });
    
    // Show test overlay on Ctrl+Shift+T
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        setShowTestOverlay(true);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* 
        ==========================================
        TEST-ZUGANG DEAKTIVIERT FÜR PRODUKTION
        Bei Bedarf wieder aktivieren durch Entfernen der Kommentare
        ==========================================
      
      {showTestOverlay && (
        <TestOverlay
          onLogin={handleLogin}
          onClose={() => setShowTestOverlay(false)}
        />
      )}
      
      <button
        onClick={() => setShowTestOverlay(true)}
        className="fixed bottom-20 right-4 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 px-6 py-3 rounded-full shadow-2xl font-bold text-base transition-transform hover:scale-105"
        data-testid="open-test-overlay"
        style={{ display: showTestOverlay ? 'none' : 'block', zIndex: 9999 }}
      >
        🔑 Test-Zugang
      </button>
      
      */}
      
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/cookies" element={<CookiePage />} />
          <Route path="/admin-setup" element={<AdminSetup onSetupComplete={handleLogin} />} />
          <Route
            path="/auth"
            element={
              user ? (
                user.role === 'admin' ? <Navigate to="/admin" /> : <Navigate to="/dashboard" />
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/onboarding"
            element={
              user && !user.onboardingComplete ? (
                <OnboardingWizard user={user} setUser={setUser} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user ? (
                user.role === 'student' ? (
                  user.onboardingComplete ? (
                    <StudentDashboard user={user} onLogout={handleLogout} setUser={setUser} />
                  ) : (
                    <Navigate to="/onboarding" />
                  )
                ) : (
                  <Navigate to="/admin" />
                )
              ) : (
                <Navigate to="/auth" />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && user.role === 'admin' ? (
                <AdminDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/auth" />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                user.role === 'admin' ? (
                  <Navigate to="/admin" />
                ) : user.onboardingComplete ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <Navigate to="/onboarding" />
                )
              ) : (
                <LandingPage />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
